import { makeStyles } from "tss-react/mui";
import {
  MiBuildPageHeader,
  MiButton,
  MiButtonGroup,
  MiDateRangePicker,
  MiIcon,
  MiLoader,
  MiTab,
} from "@miplatform/components";
import { BUTTON_VARIANTS, CALENDAR_TYPES, VIEWS } from "@miplatform/constants";
import { getDate } from "@miplatform/utils";
import {
  mdiCalendar,
  mdiCircle,
  mdiHeadset,
  mdiListBox,
  mdiMap,
  mdiPlus,
} from "@mdi/js";
import { DatePicker } from "@mibuildweb/components";
import { Box, Typography } from "@mui/material";
import { attachEventTimes } from "../../utils";
import { AttemptSearch } from "../AttemptSearch/AttemptSearch";
import { SCHEDULE_VIEWS } from "../scheduleViews";

export const DispatchHeader = ({
  isSignalRConnected,
  tabs,
  calendarView,
  date,
  setDate,
  onDateChange,
  dateRange,
  activeTab,
  setActiveTab,
  scheduleView,
  setScheduleView,
  setCalendarView,
  isLoading,
  setIsCreateNewOrderDrawerOpen,
  appendOrderTypeColor,
  viewConfig,
  buckets,
  onSearchSelect,
}) => {
  const { classes, theme } = useStyles();
  const isDispatchScheduler =
    viewConfig?.calendarType === CALENDAR_TYPES.SCHEDULER;

  const renderLeftComponent = () => {
    if (!isDispatchScheduler) {
      return null;
    }
    return (
      <Box className={classes.connectedContainer}>
        <MiIcon
          color={
            isSignalRConnected
              ? theme.palette.green.main
              : theme.palette.red.dark
          }
          size={0.4}
          path={mdiCircle}
        />
        <Typography
          fontSize={theme.fontSize.small}
          color={
            isSignalRConnected
              ? theme.palette.green.main
              : theme.palette.red.dark
          }
        >
          {isSignalRConnected ? "Connected" : "Disconnected"}
        </Typography>
      </Box>
    );
  };

  function renderCenterComponent() {
    if (!isDispatchScheduler) {
      return null;
    }
    return (
      <Box className={classes.dateRangeContainer}>
        {calendarView === VIEWS.DAY ? (
          <DatePicker
            date={date}
            setDate={setDate}
            onDateChange={onDateChange}
          />
        ) : (
          <MiDateRangePicker
            showShortCutItems
            shortcutItems={[
              {
                label: "This Week",
                getValue: () => {
                  const today = getDate();
                  return [
                    today.clone().startOf("week"),
                    today.clone().endOf("week").add(2, "day"),
                  ];
                },
              },
              {
                label: "Last Week",
                getValue: () => {
                  const today = getDate();
                  const prevWeek = today.clone().subtract(1, "week");
                  return [
                    prevWeek.clone().startOf("week"),
                    prevWeek.clone().endOf("week"),
                  ];
                },
              },
              {
                label: "Next Week",
                getValue: () => {
                  const today = getDate();
                  const nextWeek = today.clone().add(1, "week");
                  return [
                    nextWeek.clone().startOf("week"),
                    nextWeek.clone().endOf("week"),
                  ];
                },
              },
            ]}
            onDateChange={onDateChange}
            dateRange={dateRange}
            exactRangeLength={7}
            calendarCount={2}
          />
        )}
        <AttemptSearch
          appendOrderTypeColor={appendOrderTypeColor}
          searchableOrderTemplateIds={viewConfig?.orderTemplateIds}
          buckets={buckets}
          onSelect={onSearchSelect}
          attachEventTimes={attachEventTimes}
        />
      </Box>
    );
  }

  function renderActionComponent() {
    if (!isDispatchScheduler) {
      return null;
    }
    return (
      <MiButton
        title="Create New"
        size="small"
        variant={BUTTON_VARIANTS.CONTAINED}
        data-testid="create-order-button"
        startIcon={{
          path: mdiPlus,
          iconColor: theme.palette.common.white,
        }}
        onClick={() => setIsCreateNewOrderDrawerOpen(true)}
      />
    );
  }

  function renderCalendarSwitch() {
    if (!isDispatchScheduler) {
      return null;
    }
    return (
      <Box>
        <MiButtonGroup
          condensed
          data={[
            {
              label: "Calendar",
              value: SCHEDULE_VIEWS.CALENDAR,
              icon: mdiCalendar,
              iconSize: 0.6,
            },
            {
              label: "List",
              value: SCHEDULE_VIEWS.LIST,
              icon: mdiListBox,
              iconSize: 0.6,
            },
            {
              label: "Map",
              value: SCHEDULE_VIEWS.MAP,
              icon: mdiMap,
              iconSize: 0.6,
            },
          ]}
          selected={scheduleView}
          handleClick={(item) => {
            setScheduleView(item.value);
            setCalendarView(VIEWS.DAY);
          }}
        />
      </Box>
    );
  }
  return (
    <Box>
      <MiBuildPageHeader
        titleIcon={
          <MiIcon path={mdiHeadset} color={theme.palette.primary.main} />
        }
        title="Dispatch"
        leftComponent={renderLeftComponent()}
        centerComponent={renderCenterComponent()}
        buttonGroupComponent={renderCalendarSwitch()}
        actionComponent={renderActionComponent()}
        hasDivider={false}
      />
      <MiTab
        tabs={tabs}
        currentTab={activeTab}
        onTabChange={setActiveTab}
        scrollButtons={false}
        chipStyle={{
          height: 18,
          minWidth: 18,
          width: "fit-content",
          borderRadius: "100%",
          "& .MuiChip-label": {
            ...theme.typography.body2,
            fontSize: theme.fontSize.xsmall,
            padding: theme.spacing(0, 1),
          },
        }}
        borderBottom
      />
      <Box className={classes.loaderContainer}>
        <MiLoader
          isLoading={isLoading}
          type="linear"
          style={{ position: "absolute" }}
        />
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  connectedContainer: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    marginLeft: theme.spacing(2),
    width: 100,
  },
  dateRangeContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  name: {
    color: theme.palette.bluegrey.main,
  },
  loaderContainer: {
    width: "100%",
    position: "relative",
    zIndex: theme.zIndex.drawer + 1,
  },
}));
